/**
 * Gate the cursor trail on capable, hardware-accelerated desktops.
 * Low-end / software-rendered machines skip it to avoid UI lag.
 */
export function canEnableCursorTrail() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;

  // Respect reduced-motion preference
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;

  // Touch-first devices don't need a mouse trail
  if (window.matchMedia('(hover: none), (pointer: coarse)').matches) return false;

  // Very low CPU / RAM (Chrome exposes deviceMemory)
  const cores = navigator.hardwareConcurrency || 0;
  if (cores > 0 && cores <= 2) return false;

  const memory = navigator.deviceMemory;
  if (typeof memory === 'number' && memory < 4) return false;

  // Data-saver / slow network — skip decorative GPU work
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (connection?.saveData) return false;
  if (connection?.effectiveType && /2g/.test(connection.effectiveType)) return false;

  // Software WebGL / no GPU acceleration → skip
  if (isSoftwareOrMissingGpu()) return false;

  return true;
}

function isSoftwareOrMissingGpu() {
  try {
    const canvas = document.createElement('canvas');
    const gl =
      canvas.getContext('webgl', { failIfMajorPerformanceCaveat: true }) ||
      canvas.getContext('experimental-webgl', { failIfMajorPerformanceCaveat: true });

    // failIfMajorPerformanceCaveat rejects many software renderers
    if (!gl) return true;

    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    if (debugInfo) {
      const renderer = String(gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || '');
      if (/swiftshader|llvmpipe|softpipe|software|microsoft basic render|gdi generic/i.test(renderer)) {
        return true;
      }
    }

    return false;
  } catch {
    return true;
  }
}
